import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  TablePagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import UpdateIcon from '@mui/icons-material/Update';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

// Contexts and Components
import { PageHeaderCard, ContentCardSkeleton } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';

// Services
import { activitiesAPI } from '../../services/api';

// Utils and Constants
import { getActivityVisualConfig, formatRelativeTime } from '../../utils/helper';

const typeConfig = {
  completed: { label: 'Completed', color: 'success' },
  created: { label: 'Created', color: 'primary' },
  updated: { label: 'Updated', color: 'secondary' },
  warning: { label: 'Warning', color: 'warning' },
};

const ActivityList = () => {
  const { getActiveCompany } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;

  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activities, setActivities] = useState([]);
  const [totalActivitiesCount, setTotalActivitiesCount] = useState(0);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [activitiesError, setActivitiesError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!activeCompanyId) {
        setActivities([]);
        setTotalActivitiesCount(0);
        setActivitiesError('');
        return;
      }

      setIsLoadingActivities(true);
      setActivitiesError('');
      try {
        const response = await activitiesAPI.getActivitiesByCompanyPaginated(activeCompanyId, {
          search: debouncedSearchTerm || undefined,
          action: typeFilter === 'all' ? undefined : typeFilter,
          page: page + 1,
          pageSize: rowsPerPage,
          sortBy: 'created_at',
          sortOrder,
        });

        const mapped = (Array.isArray(response.items) ? response.items : []).map((activity) => {
          const visuals = getActivityVisualConfig(activity.action, activity.entityType, activity.metadata);
          return {
            ...activity,
            icon: visuals.icon,
            color: visuals.color,
            type: visuals.type,
            user: activity.actor?.name || 'System',
            time: formatRelativeTime(activity.createdAt),
          };
        });

        setActivities(mapped);
        setTotalActivitiesCount(response.total || 0);
      } catch (error) {
        setActivities([]);
        setTotalActivitiesCount(0);
        setActivitiesError(error.message || 'Failed to fetch activities');
      } finally {
        setIsLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [activeCompanyId, debouncedSearchTerm, typeFilter, sortOrder, page, rowsPerPage]);

  // Statistics
  const stats = useMemo(() => {
    const total = activities.length;
    const completed = activities.filter((a) => a.type === 'completed').length;
    const created = activities.filter((a) => a.type === 'created').length;
    const warnings = activities.filter((a) => a.type === 'warning').length;
    return { total, completed, created, warnings };
  }, [activities]);

  const filteredActivities = activities;

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, typeFilter, sortOrder]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Header */}
      <PageHeaderCard
        title="Activity Log"
        subtitle="View all recent activities and updates"
        titleVariant={isMobile ? 'h5' : 'h4'}
        titleFontWeight={700}
      />

      {/* Statistics Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: { xs: 1.5, sm: 2 },
        mb: 3
      }}>
        {[
          { title: 'Total Activities', value: stats.total, icon: HistoryIcon, color: '#1976d2' },
          { title: 'Completed', value: stats.completed, icon: CheckCircleIcon, color: '#2e7d32' },
          { title: 'Created', value: stats.created, icon: NewReleasesIcon, color: '#9c27b0' },
          { title: 'Warnings', value: stats.warnings, icon: NotificationsActiveIcon, color: '#ed6c02' },
        ].map((item) => {
          const IconComponent = item.icon;
          return (
            <Card
              key={item.title}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                height: { xs: 100, sm: 120 },
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, mb: 0.5, fontWeight: 500 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.2 }}>
                      {item.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: { xs: 1, sm: 1.5 },
                      borderRadius: 2,
                      backgroundColor: `${item.color}12`,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconComponent sx={{ color: item.color, fontSize: { xs: 24, sm: 28 } }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
          Filter
        </Typography>
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
          }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="updated">Updated</MenuItem>
                <MenuItem value="deleted">Deleted</MenuItem>
                <MenuItem value="status_changed">Status Changed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortOrder}
                label="Sort"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="desc">Latest First</MenuItem>
                <MenuItem value="asc">Oldest First</MenuItem>
              </Select>
            </FormControl>
            {(searchTerm || typeFilter !== 'all') && (
              <Chip
                label={`${totalActivitiesCount} results`}
                size={isMobile ? 'small' : 'medium'}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Paper>
      </Box>

      {/* Activities List */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 8,
              height: 24,
              borderRadius: 1,
              backgroundColor: 'primary.main',
            }} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
              Activities
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setShowSearch(!showSearch)}
              sx={{
                color: showSearch ? 'primary.main' : 'text.secondary',
                '&:hover': { backgroundColor: 'primary.50' }
              }}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {totalActivitiesCount} activities
            </Typography>
          </Box>
        </Box>

        {/* Collapsible Search */}
        <Collapse in={showSearch}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
            <TextField
              size="small"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </Collapse>

        {/* Activities Timeline */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
          {activitiesError && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
              {activitiesError}
            </Typography>
          )}
          {isLoadingActivities ? (
            <ContentCardSkeleton lines={6} />
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* Timeline line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 15, sm: 17 },
                  top: 20,
                  bottom: 20,
                  width: 2,
                  backgroundColor: 'grey.200',
                  borderRadius: 1,
                }}
              />
              {filteredActivities.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <UpdateIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No activities found</Typography>
                </Box>
              ) : (
                filteredActivities.map((activity) => {
                  const IconComponent = activity.icon;
                  return (
                    <Box
                      key={activity.id}
                      onClick={() => navigate(`/activities/${activity.id}`)}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        py: 1.5,
                        px: 1,
                        position: 'relative',
                        pl: 0,
                        transition: 'all 0.15s ease',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'grey.50',
                        },
                      }}
                    >
                      {/* Timeline dot */}
                      <Box
                        sx={{
                          width: { xs: 32, sm: 36 },
                          height: { xs: 32, sm: 36 },
                          borderRadius: '50%',
                          backgroundColor: 'background.paper',
                          border: '3px solid',
                          borderColor: activity.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          zIndex: 1,
                        }}
                      >
                        <IconComponent sx={{ color: activity.color, fontSize: { xs: 14, sm: 16 } }} />
                      </Box>
                      <Box sx={{ flex: 1, pt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                            {activity.message}
                          </Typography>
                          <Chip
                            label={typeConfig[activity.type]?.label || activity.type}
                            color={typeConfig[activity.type]?.color || 'default'}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {activity.user} - {activity.time}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          )}
        </Box>

        {!isLoadingActivities && filteredActivities.length > 0 && (
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: 1 }}>
            <TablePagination
              component="div"
              count={totalActivitiesCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[20, 50, 100]}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default ActivityList;
